import type {
  OptionGroup,
  OptionRendererAction,
  Product,
  RendererCommand,
  RendererSurface,
  SelectOption,
  SelectedOptions,
  Swatch,
} from "@/configurator/domain/models/types";
import { getSelectedOptionId, getSelectedOptionIds } from "./selection";

// Fallback fill for the 2D placeholder when the product has no swatch group.
const NEUTRAL_SWATCH = "#D6D3D1";

function getRendererAction(
  group: OptionGroup,
): OptionRendererAction | undefined {
  if (group.renderer) return group.renderer;
  return group.method ? { method: group.method } : undefined;
}

function getOptionModelName(option: SelectOption): string {
  return option.modelName ?? option.id;
}

/** Only the fields the renderer paints with — see `RendererSurface`. */
function toSurface(option: Swatch): RendererSurface {
  return {
    texture: option.texture,
    normalMap: option.normalMap,
    bumpMap: option.bumpMap,
    reflexMap: option.reflexMap,
    material: option.material,
    applyTo: option.applyTo,
  };
}

export function buildRendererCommands(
  product: Product | undefined,
  selected: SelectedOptions,
): RendererCommand[] {
  if (!product) return [];

  const commands: RendererCommand[] = [];

  for (const group of product.optionGroups) {
    // Multi-select groups toggle each part on/off independently, so they map to
    // one visibility command per option rather than a single "show one" command.
    if (group.kind === "gridOptions") {
      const selectedIds = getSelectedOptionIds(group, selected);
      for (const option of group.options) {
        commands.push({
          method: "setPartVisibility",
          modelName: getOptionModelName(option),
          visible: selectedIds.includes(option.id),
        });
      }
      continue;
    }

    const renderer = getRendererAction(group);
    if (!renderer) continue;

    if (
      renderer.method === "applyTexture" ||
      renderer.method === "switchMaterials"
    ) {
      if (group.kind === "swatches") {
        const optionId = getSelectedOptionId(group, selected);
        const option = group.options.find((item) => item.id === optionId);
        if (!option) continue;
        commands.push({ method: renderer.method, surface: toSurface(option) });
      }
      continue;
    }

    if (renderer.method === "applyVariant") {
      if (group.kind === "swatches") {
        const optionId = getSelectedOptionId(group, selected);
        const option = group.options.find((item) => item.id === optionId);
        if (option?.variant) {
          commands.push({
            method: "applyVariant",
            variantName: option.variant,
          });
        }
      }
      continue;
    }

    const optionId = getSelectedOptionId(group, selected);
    const option = group.options.find((item) => item.id === optionId);
    if (!option) continue;

    if (renderer.method === "showPart") {
      if (group.kind !== "swatches") {
        commands.push({
          method: "showPart",
          partName: renderer.target ?? group.id,
          modelName: getOptionModelName(option),
        });
      }
      continue;
    }

    commands.push({
      method: "applyMaterial",
      materialId: option.id,
      target: renderer.target ?? group.id,
    });
  }

  return commands;
}

/** Fill the 2D placeholder paints, taken from the first swatch group. */
export function getPrimarySwatchColor(
  product: Product | undefined,
  selected: SelectedOptions,
): string {
  const swatchGroup = product?.optionGroups.find(
    (group) => group.kind === "swatches",
  );
  if (!swatchGroup || swatchGroup.kind !== "swatches") return NEUTRAL_SWATCH;

  const optionId = getSelectedOptionId(swatchGroup, selected);
  return (
    swatchGroup.options.find((option) => option.id === optionId)?.color ??
    NEUTRAL_SWATCH
  );
}
